import { observable } from "@legendapp/state";
import type { DataType } from "@repo/lanes";
import type { ColumnsConfig } from "@repo/shared/rift/types";
import type { GraphConfig } from "@repo/shared/types/graph-config";
import { Button } from "@repo/ui/ui/button";
import { Spinner } from "@repo/ui/ui/spinner";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { RiftChart } from "~/components/rift-chart";
import { useRiftQuery } from "~/stores/dataset-rifts";
import { RunPayloadError } from "~/stores/datasets/rift-dataset-payload.query";
import { useRunChartRift } from "~/stores/run-results/run-chart-rift";
import { reexecuteRun } from "~/stores/run-results/run-reexecute";
import { RunResultActions } from "./run-result-actions";

/**
 * The `createChart` tool output persisted on a chat message: the run the chart
 * plots, its agent-authored `graphConfig`, and the run's column schema (used to
 * build chart SQL client-side without a dataset). `error` is set when the
 * config failed server-side validation against the run's columns.
 */
export type CreateChartToolOutput = {
  runId?: string;
  graphConfig: GraphConfig;
  columns: { name: string; type: string }[];
  error?: string;
};

/** Fixed chart height in chat — there is no board grid cell to fill. */
const CHART_HEIGHT_PX = 320;

/**
 * Renders a chat `createChart` result as a live chart. The chart hydrates the
 * full immutable run from `runId` through the run-only chart rift pipeline
 * (`useRunChartRift` → `useRiftQuery` → `ensureRunPayload`) and the shared
 * `RiftChart` renderer — the same AG Charts path board charts use, minus the
 * dataset/board coupling. The user can promote it to a board (as a GRAPH
 * block) from the actions menu.
 */
export const RunResultChart = ({
  output,
  title,
}: {
  output: CreateChartToolOutput;
  /** The createChart tool's `title` input, used to name a saved block. */
  title?: string;
}) => {
  const [rerunning, setRerunning] = useState(false);

  const runId = output.runId ?? null;

  const columnDefs = useMemo<Record<string, DataType>>(
    () =>
      Object.fromEntries(
        output.columns.map((col) => [col.name, col.type as DataType]),
      ),
    [output.columns],
  );

  // Static per message: wrap the persisted config in observables once so
  // `RiftChart` (which expects Legend observables) can read them reactively.
  const [graphConfig$] = useState(() => observable(output.graphConfig));
  const [columnsConfig$] = useState(() => observable<ColumnsConfig>({}));

  const riftId = useRunChartRift(runId, output.graphConfig, columnDefs);
  const query = useRiftQuery(riftId);

  const payloadError =
    query.error instanceof RunPayloadError ? query.error : null;
  const expired = payloadError?.reason === "expired";
  const loadFailed = query.isError && !expired;

  const handleRerun = async () => {
    if (!runId) return;
    setRerunning(true);
    try {
      await reexecuteRun(runId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to re-run the query.",
      );
    } finally {
      setRerunning(false);
    }
  };

  // Server-side validation failure — the config can't be charted.
  if (output.error) {
    return (
      <div className="mt-1 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-destructive text-xs">
        {output.error}
      </div>
    );
  }

  if (!runId) {
    return (
      <div className="mt-1 rounded-md border p-3 text-muted-foreground text-xs">
        This chart is no longer available.
      </div>
    );
  }

  return (
    <div className="mt-1 overflow-hidden rounded-md border">
      <div className="flex items-center justify-between gap-2 px-2 py-1.5">
        <span className="truncate font-medium text-foreground text-xs">
          {title?.trim() || "Chart"}
        </span>
        <RunResultActions
          runId={runId}
          name={title ?? ""}
          graphConfig={output.graphConfig}
        />
      </div>

      {expired && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/40 p-2 text-muted-foreground text-xs">
          <span>
            This result has expired. Re-run the query to load the chart.
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={rerunning}
            onClick={handleRerun}
          >
            {rerunning ? <Spinner className="size-3.5" /> : null}
            Re-run
          </Button>
        </div>
      )}

      {loadFailed && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/40 p-2 text-muted-foreground text-xs">
          <span>Couldn't load the chart.</span>
          <Button size="sm" variant="outline" onClick={() => query.refetch()}>
            Retry
          </Button>
        </div>
      )}

      {expired ? (
        <div
          className="flex items-center justify-center p-6 text-muted-foreground text-xs"
          style={{ height: CHART_HEIGHT_PX }}
        >
          Re-run the query to load the chart.
        </div>
      ) : (
        <div style={{ height: CHART_HEIGHT_PX }}>
          {riftId ? (
            <RiftChart
              riftId={riftId}
              columnsConfig$={columnsConfig$}
              graphConfig$={graphConfig$}
            />
          ) : null}
        </div>
      )}
    </div>
  );
};
